<template>
  <div class="pt-4 pb-4 px-6">
    <div v-if="canAdd" v-show="!showingAdd">
      <Button @click="showAdd" :is-primary="false" :is-ghost="true" class="w-full">Add New Address</Button>
    </div>
    <fieldset v-show="showingAdd && (canAdd || (canEdit && passedDeliveryAddress))">
      <div>
        <div class="flex justify-between">
          <legend class="block text-md font-semibold tracking-tighter leading-5 text-gray-700">
            {{ title }}
          </legend>
          <button
            v-if="passedDeliveryAddress"
            class="ml-2 inline-flex items-center shadow-sm p-1.5 border border-gray-300 text-sm leading-5 font-medium rounded-full bg-white hover:border-blue-600 transition-all duration-150 group-hover:opacity-100 text-gray-500 hover:text-blue-600"
            @click="updateCloseClicked()"
          >
            <XIcon class="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <legend class="block mt-1 mb-3 text-sm tracking-wide leading-5 text-gray-600">
          Enter your address details.
        </legend>
      </div>

      <form class="mt-2 bg-white rounded-md shadow-sm">
        <div class="-mt-px flex">
          <div class="w-3/5 flex-1 min-w-0">
            <input
              id="address-line1"
              ref="addr1"
              v-model="address1"
              type="text"
              name="address-line1"
              autocomplete="address-line1"
              autocapitalize="words"
              aria-label="Street address"
              class="block w-full rounded-none rounded-tl-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              placeholder="Street address"
            />
          </div>
          <div class="w-2/5 -ml-px min-w-0">
            <input
              id="address-line2"
              ref="addr2"
              v-model="address2"
              type="text"
              autocomplete="address-line2"
              name="address-line2"
              aria-label="Unit"
              class="block w-full rounded-none rounded-tr-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              placeholder="Unit"
            />
          </div>
        </div>
        <div class="-mt-px flex">
          <div class="w-3/10 sm:w-2/5 flex-1 min-w-0">
            <input
              id="address-level2"
              v-model="city"
              type="text"
              autocomplete="address-level2"
              autocapitalize="words"
              name="address-level2"
              aria-label="City"
              class="block w-full rounded-none border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              placeholder="City"
            />
          </div>
          <div class="w-3/10 sm:w-1/5 -ml-px min-w-0">
            <select
              id="state"
              v-model="state"
              :key="`state-${merchantCountry}-${state}`"
              autocomplete="address-level1"
              name="state"
              class="block w-full rounded-none border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              placeholder="State"
              @change="onStateChange"
            >
              <option value="" disabled selected>{{ statePlaceholder }}</option>
              <template v-if="merchantCountry === 'USA'">
                <option value="AK">AK</option>
                <option value="AZ">AZ</option>
                <option value="AL">AL</option>
                <option value="AR">AR</option>
                <option value="CA">CA</option>
                <option value="CO">CO</option>
                <option value="CT">CT</option>
                <option value="DC">DC</option>
                <option value="DE">DE</option>
                <option value="FL">FL</option>
                <option value="GA">GA</option>
                <option value="HI">HI</option>
                <option value="ID">ID</option>
                <option value="IL">IL</option>
                <option value="IN">IN</option>
                <option value="IA">IA</option>
                <option value="KS">KS</option>
                <option value="KY">KY</option>
                <option value="LA">LA</option>
                <option value="ME">ME</option>
                <option value="MD">MD</option>
                <option value="MA">MA</option>
                <option value="MI">MI</option>
                <option value="MN">MN</option>
                <option value="MS">MS</option>
                <option value="MO">MO</option>
                <option value="MT">MT</option>
                <option value="NE">NE</option>
                <option value="NV">NV</option>
                <option value="NH">NH</option>
                <option value="NJ">NJ</option>
                <option value="NM">NM</option>
                <option value="NY">NY</option>
                <option value="NC">NC</option>
                <option value="ND">ND</option>
                <option value="OH">OH</option>
                <option value="OK">OK</option>
                <option value="OR">OR</option>
                <option value="PA">PA</option>
                <option value="RI">RI</option>
                <option value="SC">SC</option>
                <option value="SD">SD</option>
                <option value="TN">TN</option>
                <option value="TX">TX</option>
                <option value="UT">UT</option>
                <option value="VT">VT</option>
                <option value="VA">VA</option>
                <option value="WA">WA</option>
                <option value="WV">WV</option>
                <option value="WI">WI</option>
                <option value="WY">WY</option>
              </template>
              <template v-if="merchantCountry === 'AUS'">
                <option value="ACT">ACT</option>
                <option value="NSW">NSW</option>
                <option value="NT">NT</option>
                <option value="QLD">QLD</option>
                <option value="SA">SA</option>
                <option value="TAS">TAS</option>
                <option value="VIC">VIC</option>
                <option value="WA">WA</option>
              </template>
              <template v-if="merchantCountry === 'CAN'">
                <option value="AB">AB</option>
                <option value="BC">BC</option>
                <option value="MB">MB</option>
                <option value="NB">NB</option>
                <option value="NL">NL</option>
                <option value="NS">NS</option>
                <option value="NT">NT</option>
                <option value="NU">NU</option>
                <option value="ON">ON</option>
                <option value="PE">PE</option>
                <option value="QC">QC</option>
                <option value="SK">SK</option>
                <option value="YT">YT</option>
              </template>
              <template v-if="merchantCountry === 'GBR'">
                <option value="BED">Bedfordshire</option>
                <option value="BERK">Berkshire</option>
                <option value="BUCK">Buckinghamshire</option>
                <option value="CAM">Cambridgeshire</option>
                <option value="CHES">Cheshire</option>
                <option value="CORN">Cornwall</option>
                <option value="CUMB">Cumbria</option>
                <option value="DERB">Derbyshire</option>
                <option value="DEV">Devon</option>
                <option value="DOR">Dorset</option>
                <option value="DUR">County Durham</option>
                <option value="ERYORK">East Riding of Yorkshire</option>
                <option value="ESX">East Sussex</option>
                <option value="ESS">Essex</option>
                <option value="GLS">Gloucestershire</option>
                <option value="GMAN">Greater Manchester</option>
                <option value="HANTS">Hampshire</option>
                <option value="HERTS">Hertfordshire</option>
                <option value="HANTS">Herefordshire</option>
                <option value="IOW">Isle of Wight</option>
                <option value="KENT">Kent</option>
                <option value="LANCS">Lancashire</option>
                <option value="LEICS">Leicestershire</option>
                <option value="LINCS">Lincolnshire</option>
                <option value="LOND">Greater London</option>
                <option value="MER">Merseyside</option>
                <option value="NORF">Norfolk</option>
                <option value="NHANTS">Northamptonshire</option>
                <option value="NTHUMB">Northumberland</option>
                <option value="NOTTS">Nottinghamshire</option>
                <option value="OXON">Oxfordshire</option>
                <option value="RUT">Rutland</option>
                <option value="SHROP">Shropshire</option>
                <option value="SOM">Somerset</option>
                <option value="STS">Staffordshire</option>
                <option value="SUFF">Suffolk</option>
                <option value="SURREY">Surrey</option>
                <option value="TYNE">Tyne and Wear</option>
                <option value="WARKS">Warwickshire</option>
                <option value="W MIDS">West Midlands</option>
                <option value="W SUSX">West Sussex</option>
                <option value="WILTS">Wiltshire</option>
                <option value="WORCS">Worcestershire</option>
                <option value="YORK">North Yorkshire</option>
                <option value="SYORK">South Yorkshire</option>
                <option value="WYORK">West Yorkshire</option>
                <option value="WALES">Wales</option>
                <option value="SCOT">Scotland</option>
                <option value="NIRE">Northern Ireland</option>
              </template>
            </select>
          </div>
          <div class="w-2/5 -ml-px min-w-0">
            <input
              id="postal-code"
              v-model="zip"
              type="text"
              autocomplete="postal-code"
              name="postal-code"
              aria-label="Postal code"
              class="block w-full rounded-none border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              placeholder="Postal code"
            />
          </div>
        </div>
        <div class="-mt-px flex">
          <div class="w-full flex-1 min-w-0">
            <input
              id="notes"
              v-model="notes"
              type="text"
              autocomplete="off"
              name="notes"
              aria-label="Notes"
              class="block w-full rounded-none rounded-bl-md rounded-br-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              placeholder="Gate code or instructions (optional)"
            />
          </div>
        </div>
      </form>

      <div class="mt-3 flex items-center justify-between">
        <div class="flex flex-col">
          <span class="text-sm font-medium text-gray-700">Set as default delivery address</span>
        </div>
        <button
          type="button"
          role="switch"
          :aria-checked="isDefault"
          @click="isDefault = !isDefault"
          :class="[
            isDefault ? 'bg-blue-600' : 'bg-gray-200',
            'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2'
          ]"
        >
          <span
            aria-hidden="true"
            :class="[
              isDefault ? 'translate-x-5' : 'translate-x-0',
              'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
            ]"
          />
        </button>
      </div>

      <div v-if="hasErrorMessage" class="mt-2 text-sm text-red-600">
        {{ errorMessage }}
      </div>

      <div class="mt-4 flex justify-end space-x-3">
        <Button @click="updateCloseClicked" :is-primary="false" :is-ghost="true">Cancel</Button>
        <Button
          :key="`button-${validAddress}-${isAddingAddress}`"
          @click="addAddress"
          :is-disabled="!validAddress || isAddingAddress"
          :is-loading="isAddingAddress"
          :is-primary="false"
          :is-ghost="true"
          class="min-w-24"
        >
          <span v-if="isAddingAddress">Saving...</span>
          <span v-else>{{ selectButtonTitle }}</span>
        </Button>
      </div>
    </fieldset>
  </div>
</template>

<script>
import { XMarkIcon as XIcon } from '@heroicons/vue/20/solid'
import { nextTick } from 'vue'
import Button from '@/components/Button.vue'
import api from '@/store/api'
import { useStore } from 'vuex'

export default {
  name: 'AddDeliveryAddressForm',
  components: {
    Button,
    XIcon
  },
  setup() {
    const store = useStore()
    return { store }
  },
  props: {
    passedDeliveryAddress: {
      type: Object,
      required: false
    },
    forceOpen: {
      type: Boolean,
      required: false,
      default: false
    },
    conversationUserPairId: {
      type: [String, Number],
      required: true
    },
    canAdd: {
      type: Boolean,
      default: false
    },
    canEdit: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    notes: '',
    isDefault: false,
    isAddingAddress: false,
    errorMessage: '',
    replacingAddressId: undefined,
    showingAdd: false,
    googlePlacesInitialized: false,
    autocompleteInstance: null
  }),
  computed: {
    statePlaceholder() {
      if (this.merchantCountry === 'USA') return 'State'
      if (this.merchantCountry === 'GBR') return 'County'
      return 'Province'
    },
    selectButtonTitle() {
      if (this.passedDeliveryAddress) {
        return 'Update & Save'
      } else {
        return 'Add & Save'
      }
    },
    title() {
      if (this.passedDeliveryAddress) {
        return 'Update an address'
      } else {
        return 'Add an address'
      }
    },
    validAddress() {
      try {
        const minZipLength = { USA: 5, AUS: 4, CAN: 7, GBR: 7 }[this.merchantCountry] || 5
        const result = this.address1 && this.city && this.state && this.zip && this.zip.length >= minZipLength

        return result
      } catch (error) {
        console.error('Validation error:', error)
        return false
      }
    },
    hasErrorMessage() {
      return this.errorMessage.length > 0
    },
    merchantCountry() {
      return this.store.getters.country || 'USA'
    }
  },
  watch: {
    state(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.errorMessage = ''
      }
    },
    passedDeliveryAddress: {
      handler(newValue) {
        if (newValue) {
          this.address1 = newValue.attributes.address1 || ''
          this.address2 = newValue.attributes.address2 || ''
          this.city = newValue.attributes.city || ''
          this.state = newValue.attributes.state || ''
          this.zip = newValue.attributes.zip || ''
          this.notes = newValue.attributes.notes || ''
          this.isDefault = !!newValue.attributes.is_default
          this.replacingAddressId = newValue.id
          this.showingAdd = true
        }
      },
      immediate: true
    },
    forceOpen: {
      handler(newValue) {
        if (newValue) {
          this.showingAdd = true
          nextTick(() => this.$refs.addr1?.focus())
        }
      },
      immediate: true
    },
    merchantCountry: {
      handler(newValue, oldValue) {
        if (newValue !== oldValue && window.google && window.google.maps) {
          this.googlePlacesInitialized = false
          if (newValue && newValue !== 'USA') {
            this.initializeGooglePlaces()
          }
        }
      }
    }
  },
  mounted() {
    this.showingAdd = this.forceOpen
  },
  methods: {
    initializeGooglePlaces() {
      if (this.googlePlacesInitialized) return

      const countryCode = { USA: 'us', AUS: 'au', CAN: 'ca', GBR: 'gb' }[this.merchantCountry]

      if (this.autocompleteInstance) {
        google.maps.event.clearInstanceListeners(this.autocompleteInstance)
        this.autocompleteInstance = null
      }

      google.maps.importLibrary('places').then(({ Autocomplete }) => {
        const autocomplete = new Autocomplete(this.$refs.addr1, {
          componentRestrictions: { country: countryCode },
          fields: ['address_components', 'formatted_address', 'geometry', 'place_id']
        })
        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace()

          this.address1 = ''
          this.address2 = ''
          this.city = ''
          this.state = ''
          this.zip = ''

          for (const comp of place.address_components) {
            for (const type of comp.types) {
              switch (type) {
                case 'street_number':
                  this.address1 = comp.long_name
                  break
                case 'route':
                  if (this.address1) {
                    this.address1 += ' ' + comp.short_name
                  } else {
                    this.address1 = comp.short_name
                  }
                  break
                case 'subpremise':
                  this.address2 = comp.long_name
                  break
                case 'locality':
                  if (!this.city) {
                    this.city = comp.long_name
                  }
                  break
                case 'sublocality_level_1':
                  if (!this.city) {
                    this.city = comp.long_name
                  }
                  break
                case 'postal_town':
                  if (!this.city) {
                    this.city = comp.long_name
                  }
                  break
                case 'administrative_area_level_1':
                  if (this.merchantCountry === 'GBR') {
                    const mappedState = this.mapUKCountyToCode(comp.long_name)
                    if (mappedState !== null) {
                      this.state = mappedState
                    }
                  } else {
                    this.state = comp.short_name
                  }
                  break
                case 'administrative_area_level_2':
                  if (this.merchantCountry === 'GBR' && !this.state) {
                    const mappedState = this.mapUKCountyToCode(comp.long_name)
                    if (mappedState !== null) {
                      this.state = mappedState
                    }
                  }
                  break
                case 'country':
                  break
                case 'postal_code':
                  this.zip = comp.long_name
                  break
              }
            }
          }

          if (!this.city && place.formatted_address) {
            const parts = place.formatted_address.split(',')
            if (parts.length > 1) {
              this.city = parts[1].trim()
            }
          }

          nextTick(() => this.$refs.addr2?.focus())
        })
        this.autocompleteInstance = autocomplete
        this.googlePlacesInitialized = true
      })
    },
    showAdd() {
      this.showingAdd = true
      nextTick(() => {
        this.$refs.addr1?.focus()
        if (window.google && window.google.maps) {
          this.googlePlacesInitialized = false
          this.initializeGooglePlaces()
        }
      })
    },
    addAddress() {
      this.isAddingAddress = true
      this.errorMessage = ''

      const addressData = {
        address1: this.address1,
        address2: this.address2,
        city: this.city,
        state: this.state,
        zip: this.zip,
        notes: this.notes,
        country: this.merchantCountry,
        is_default: this.isDefault === true
      }

      if (this.replacingAddressId) {
        api
          .call(
            'PUT',
            `conversation_user_pairs/${this.conversationUserPairId}/customer/delivery_address/${this.replacingAddressId}`,
            {
              delivery_address: addressData
            }
          )
          .then(
            response => {
              this.isAddingAddress = false
              this.clearFields()
              this.showingAdd = false
              this.$emit('delivery-address-saved')
            },
            error => {
              this.isAddingAddress = false
              this.errorMessage = 'Failed to save address. Please try again.'
            }
          )
      } else {
        api
          .call('POST', `conversation_user_pairs/${this.conversationUserPairId}/customer/delivery_address`, {
            delivery_address: addressData
          })
          .then(
            response => {
              this.isAddingAddress = false
              this.clearFields()
              this.showingAdd = false
              this.$emit('delivery-address-saved')
            },
            error => {
              this.isAddingAddress = false
              this.errorMessage = 'Failed to save address. Please try again.'
            }
          )
      }
    },
    clearFields() {
      this.address1 = ''
      this.address2 = ''
      this.city = ''
      this.state = ''
      this.zip = ''
      this.notes = ''
      this.isDefault = false
      this.replacingAddressId = undefined
    },

    mapUKCountyToCode(countyName) {
      const countryNames = ['England', 'Scotland', 'Wales', 'Northern Ireland', 'United Kingdom', 'UK']
      if (countryNames.includes(countyName)) {
        return null
      }

      const countyMapping = {
        Bedfordshire: 'BED',
        Berkshire: 'BERK',
        Buckinghamshire: 'BUCK',
        Cambridgeshire: 'CAM',
        Cheshire: 'CHES',
        Cornwall: 'CORN',
        Cumbria: 'CUMB',
        Derbyshire: 'DERB',
        Devon: 'DEV',
        Dorset: 'DOR',
        'County Durham': 'DUR',
        'East Riding of Yorkshire': 'ERYORK',
        'East Sussex': 'ESX',
        Essex: 'ESS',
        Gloucestershire: 'GLS',
        'Greater Manchester': 'GMAN',
        Hampshire: 'HANTS',
        Hertfordshire: 'HERTS',
        Herefordshire: 'HANTS',
        'Isle of Wight': 'IOW',
        Kent: 'KENT',
        Lancashire: 'LANCS',
        Leicestershire: 'LEICS',
        Lincolnshire: 'LINCS',
        'Greater London': 'LOND',
        Merseyside: 'MER',
        Norfolk: 'NORF',
        Northamptonshire: 'NHANTS',
        Northumberland: 'NTHUMB',
        Nottinghamshire: 'NOTTS',
        Oxfordshire: 'OXON',
        Rutland: 'RUT',
        Shropshire: 'SHROP',
        Somerset: 'SOM',
        Staffordshire: 'STS',
        Suffolk: 'SUFF',
        Surrey: 'SURREY',
        'Tyne and Wear': 'TYNE',
        Warwickshire: 'WARKS',
        'West Midlands': 'W MIDS',
        'West Sussex': 'W SUSX',
        Wiltshire: 'WILTS',
        Worcestershire: 'WORCS',
        'North Yorkshire': 'YORK',
        'South Yorkshire': 'SYORK',
        'West Yorkshire': 'WYORK',
        Wales: 'WALES',
        Scotland: 'SCOT',
        'Northern Ireland': 'NIRE'
      }

      if (countyMapping[countyName]) {
        return countyMapping[countyName]
      }

      const lowerCountyName = countyName.toLowerCase()
      for (const [fullName, code] of Object.entries(countyMapping)) {
        if (lowerCountyName.includes(fullName.toLowerCase()) || fullName.toLowerCase().includes(lowerCountyName)) {
          return code
        }
      }

      return null
    },

    updateCloseClicked() {
      this.clearFields()
      this.showingAdd = false
      this.$emit('delivery-address-edit-canceled')
    }
  }
}
</script>
